import { Box, CircularProgress, type TextFieldProps } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { mergeSlotProps } from '@mui/material/utils';

type GetInputSlotPropsParams = {
  inputSlotProps: AutocompleteRenderInputParams['slotProps'];
  textFieldSlotProps?: TextFieldProps['slotProps'];
  loading?: boolean;
  readOnly?: boolean;
};

// Autocomplete leaves unused keys (e.g. startAdornment) as undefined, which would override caller values when spread
const withoutUndefined = <T extends object>(props: T): Partial<T> =>
  Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined)) as Partial<T>;

// Autocomplete's own props (refs, ids, value, adornments) take precedence over the caller's, which take precedence over our defaults
export const getInputSlotProps = ({
  inputSlotProps,
  textFieldSlotProps,
  loading,
  readOnly,
}: GetInputSlotPropsParams): TextFieldProps['slotProps'] => {
  const { endAdornment } = inputSlotProps.input;
  const hasEndAdornment = loading || endAdornment != null;

  return {
    ...textFieldSlotProps,
    htmlInput: mergeSlotProps(
      withoutUndefined({
        ...inputSlotProps.htmlInput,
        // Autocomplete always sets htmlInput.readOnly from its own readOnly prop (false by default), which would win over input.readOnly
        readOnly: readOnly || inputSlotProps.htmlInput.readOnly || undefined,
      }),
      textFieldSlotProps?.htmlInput ?? {}
    ),
    inputLabel: mergeSlotProps(
      withoutUndefined(inputSlotProps.inputLabel),
      mergeSlotProps(textFieldSlotProps?.inputLabel, { disableAnimation: true, shrink: true })
    ),
    input: mergeSlotProps(
      {
        ...withoutUndefined(inputSlotProps.input),
        // Only claim the slot when there is something to show, so a caller's endAdornment survives otherwise
        ...(hasEndAdornment && {
          endAdornment: (
            <>
              {loading && (
                <Box sx={{ pr: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {endAdornment}
            </>
          ),
        }),
        ...(readOnly && { readOnly: true }),
      },
      mergeSlotProps(textFieldSlotProps?.input, { disableUnderline: true })
    ),
  };
};
